import { useState } from 'react'
import { 
  Users, 
  Crown,
  Shield,
  Heart,
  MessageSquare,
  Sword,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  Clock
} from 'lucide-react'
import { Button } from '../components/ui/button'

interface Faction {
  id: string
  name: string
  leader: string
  relationship: 'allied' | 'neutral' | 'hostile' | 'at_war'
  influence: number
  military: number
  economy: number
  territory: number
  color: string
}

interface Treaty {
  id: string
  factionId: string
  factionName: string
  type: 'trade' | 'non_aggression' | 'alliance' | 'mutual_defense'
  status: 'active' | 'pending' | 'expired' | 'violated'
  duration: string
  benefits: string[]
}

const Diplomacy = () => {
  const [factions] = useState<Faction[]>([
    {
      id: '1',
      name: 'Zephyrian Empire',
      leader: 'Emperor Kaz\'thul',
      relationship: 'allied',
      influence: 85,
      military: 78,
      economy: 92,
      territory: 67,
      color: 'blue'
    },
    {
      id: '2',
      name: 'Voidborn Collective',
      leader: 'Prime Node Zero',
      relationship: 'neutral',
      influence: 72,
      military: 95,
      economy: 65,
      territory: 54,
      color: 'purple'
    },
    {
      id: '3',
      name: 'Solaris Union',
      leader: 'Chancellor Morgan Vale',
      relationship: 'hostile',
      influence: 68,
      military: 45,
      economy: 88,
      territory: 73,
      color: 'yellow'
    },
    {
      id: '4',
      name: 'Crimson Raiders',
      leader: 'Warlord Thane',
      relationship: 'at_war',
      influence: 43,
      military: 89,
      economy: 32,
      territory: 38,
      color: 'red'
    }
  ])

  const [treaties] = useState<Treaty[]>([
    {
      id: '1',
      factionId: '1',
      factionName: 'Zephyrian Empire',
      type: 'alliance',
      status: 'active',
      duration: '2 years remaining',
      benefits: ['Military Support', 'Research Sharing', '+25% Trade']
    },
    {
      id: '2',
      factionId: '2',
      factionName: 'Voidborn Collective',
      type: 'non_aggression',
      status: 'pending',
      duration: 'Awaiting approval',
      benefits: ['No Hostile Actions', 'Safe Passage']
    },
    {
      id: '3',
      factionId: '3',
      factionName: 'Solaris Union',
      type: 'trade',
      status: 'violated',
      duration: 'Suspended',
      benefits: ['Resource Exchange', 'Economic Boost']
    }
  ])

  const [activeTab, setActiveTab] = useState('overview')

  const getRelationshipColor = (relationship: string) => {
    switch (relationship) {
      case 'allied': return 'text-green-400 bg-green-600/20'
      case 'neutral': return 'text-gray-400 bg-gray-600/20'
      case 'hostile': return 'text-orange-400 bg-orange-600/20'
      case 'at_war': return 'text-red-400 bg-red-600/20'
      default: return 'text-gray-400 bg-gray-600/20'
    }
  }

  const getRelationshipIcon = (relationship: string) => {
    switch (relationship) {
      case 'allied': return Heart
      case 'neutral': return Users
      case 'hostile': return AlertTriangle
      case 'at_war': return Sword
      default: return Users
    }
  }

  const getTreatyIcon = (type: string) => {
    switch (type) {
      case 'alliance': return Crown
      case 'trade': return TrendingUp
      case 'non_aggression': return Shield
      case 'mutual_defense': return Sword
      default: return Users
    }
  }

  const getTreatyStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'text-green-400 bg-green-600/20'
      case 'pending': return 'text-yellow-400 bg-yellow-600/20'
      case 'expired': return 'text-gray-400 bg-gray-600/20'
      case 'violated': return 'text-red-400 bg-red-600/20'
      default: return 'text-gray-400 bg-gray-600/20'
    }
  }

  const getFactionColorClass = (color: string) => {
    switch (color) {
      case 'blue': return 'from-blue-500 to-blue-600'
      case 'purple': return 'from-purple-500 to-purple-600'
      case 'yellow': return 'from-yellow-500 to-yellow-600'
      case 'red': return 'from-red-500 to-red-600'
      default: return 'from-gray-500 to-gray-600'
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-purple-900 to-slate-900 pt-16">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2 flex items-center">
              <Users className="w-10 h-10 mr-3 text-blue-400" />
              Diplomatic Relations
            </h1>
            <p className="text-gray-400">Manage alliances, treaties, and galactic politics</p>
          </div>
          <Button className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
            <MessageSquare className="w-4 h-4 mr-2" />
            Send Message
          </Button>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-4 border border-white/10">
            <div className="flex items-center justify-between mb-2">
              <Heart className="w-6 h-6 text-green-400" />
              <span className="text-xs text-gray-400">ALLIES</span>
            </div>
            <div className="text-2xl font-bold text-white">
              {factions.filter(f => f.relationship === 'allied').length}
            </div>
          </div>
          
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-4 border border-white/10">
            <div className="flex items-center justify-between mb-2">
              <Users className="w-6 h-6 text-gray-400" />
              <span className="text-xs text-gray-400">NEUTRAL</span>
            </div>
            <div className="text-2xl font-bold text-white">
              {factions.filter(f => f.relationship === 'neutral').length}
            </div>
          </div>
          
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-4 border border-white/10">
            <div className="flex items-center justify-between mb-2">
              <AlertTriangle className="w-6 h-6 text-orange-400" />
              <span className="text-xs text-gray-400">HOSTILE</span>
            </div>
            <div className="text-2xl font-bold text-white">
              {factions.filter(f => f.relationship === 'hostile').length}
            </div>
          </div>
          
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-4 border border-white/10">
            <div className="flex items-center justify-between mb-2">
              <Sword className="w-6 h-6 text-red-400" />
              <span className="text-xs text-gray-400">AT WAR</span>
            </div>
            <div className="text-2xl font-bold text-white">
              {factions.filter(f => f.relationship === 'at_war').length}
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex space-x-1 mb-6">
          {[
            { id: 'overview', label: 'Factions Overview' },
            { id: 'treaties', label: 'Treaties & Agreements' },
            { id: 'negotiations', label: 'Active Negotiations' }
          ].map((tab) => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? 'default' : 'outline'}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </Button>
          ))}
        </div>

        {/* Content based on active tab */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {factions.map((faction) => {
              const RelationshipIcon = getRelationshipIcon(faction.relationship)
              return (
                <div key={faction.id} className="bg-slate-800/50 backdrop-blur-sm rounded-lg border border-white/10 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-4">
                      <div className={`w-12 h-12 bg-gradient-to-r ${getFactionColorClass(faction.color)} rounded-lg flex items-center justify-center`}>
                        <Crown className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">{faction.name}</h3>
                        <p className="text-gray-400">{faction.leader}</p>
                      </div>
                    </div>
                    <div className={`flex items-center space-x-2 px-3 py-1 rounded-full text-sm ${getRelationshipColor(faction.relationship)}`}>
                      <RelationshipIcon className="w-4 h-4" />
                      <span className="capitalize">{faction.relationship.replace('_', ' ')}</span>
                    </div>
                  </div>

                  {/* Faction Stats */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-400 text-sm">Influence</span>
                        <span className="text-white text-sm">{faction.influence}%</span>
                      </div>
                      <div className="w-full bg-slate-700 rounded-full h-2">
                        <div 
                          className="bg-purple-500 h-2 rounded-full"
                          style={{ width: `${faction.influence}%` }}
                        ></div>
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-400 text-sm">Military</span>
                        <span className="text-white text-sm">{faction.military}%</span>
                      </div>
                      <div className="w-full bg-slate-700 rounded-full h-2">
                        <div 
                          className="bg-red-500 h-2 rounded-full"
                          style={{ width: `${faction.military}%` }}
                        ></div>
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-400 text-sm">Economy</span>
                        <span className="text-white text-sm">{faction.economy}%</span>
                      </div>
                      <div className="w-full bg-slate-700 rounded-full h-2">
                        <div 
                          className="bg-yellow-500 h-2 rounded-full"
                          style={{ width: `${faction.economy}%` }}
                        ></div>
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-400 text-sm">Territory</span>
                        <span className="text-white text-sm">{faction.territory}%</span>
                      </div>
                      <div className="w-full bg-slate-700 rounded-full h-2">
                        <div 
                          className="bg-green-500 h-2 rounded-full"
                          style={{ width: `${faction.territory}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" size="sm">
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Contact
                    </Button>
                    <Button variant="outline" size="sm">
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Negotiate
                    </Button>
                    <Button variant="outline" size="sm">
                      <TrendingUp className="w-4 h-4 mr-2" />
                      Trade
                    </Button>
                    {faction.relationship === 'hostile' || faction.relationship === 'at_war' ? (
                      <Button variant="outline" size="sm" className="text-red-400 border-red-400/30 hover:bg-red-600/10">
                        <Sword className="w-4 h-4 mr-2" />
                        Declare War
                      </Button>
                    ) : (
                      <Button variant="outline" size="sm" className="text-green-400 border-green-400/30 hover:bg-green-600/10">
                        <Heart className="w-4 h-4 mr-2" />
                        Alliance
                      </Button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {activeTab === 'treaties' && (
          <div className="space-y-4">
            {treaties.map((treaty) => {
              const TreatyIcon = getTreatyIcon(treaty.type)
              const StatusIcon = treaty.status === 'active' ? CheckCircle : 
                               treaty.status === 'pending' ? Clock : AlertTriangle
              
              return (
                <div key={treaty.id} className="bg-slate-800/50 backdrop-blur-sm rounded-lg border border-white/10 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
                        <TreatyIcon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white capitalize">
                          {treaty.type.replace('_', ' ')} Agreement
                        </h3>
                        <p className="text-gray-400">with {treaty.factionName}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className={`flex items-center space-x-2 px-3 py-1 rounded-full text-sm ${getTreatyStatusColor(treaty.status)}`}>
                        <StatusIcon className="w-4 h-4" />
                        <span className="capitalize">{treaty.status}</span>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-gray-400">Duration</div>
                        <div className="text-white">{treaty.duration}</div>
                      </div>
                    </div>
                  </div>

                  {/* Treaty Benefits */}
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-gray-300 mb-2">Benefits:</h4>
                    <div className="flex flex-wrap gap-2">
                      {treaty.benefits.map((benefit, index) => (
                        <span key={index} className="px-3 py-1 bg-slate-700/50 text-white text-sm rounded-full">
                          {benefit}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Treaty Actions */}
                  <div className="flex flex-wrap gap-2">
                    {treaty.status === 'active' && (
                      <>
                        <Button variant="outline" size="sm">
                          <TrendingUp className="w-4 h-4 mr-2" />
                          Extend
                        </Button>
                        <Button variant="outline" size="sm">
                          <MessageSquare className="w-4 h-4 mr-2" />
                          Modify
                        </Button>
                        <Button variant="outline" size="sm" className="text-red-400 border-red-400/30">
                          <TrendingDown className="w-4 h-4 mr-2" />
                          Terminate
                        </Button>
                      </>
                    )}
                    {treaty.status === 'pending' && (
                      <>
                        <Button variant="outline" size="sm" className="text-green-400 border-green-400/30">
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Approve
                        </Button>
                        <Button variant="outline" size="sm" className="text-red-400 border-red-400/30">
                          <TrendingDown className="w-4 h-4 mr-2" />
                          Reject
                        </Button>
                      </>
                    )}
                    {treaty.status === 'violated' && (
                      <Button variant="outline" size="sm">
                        <MessageSquare className="w-4 h-4 mr-2" />
                        Renegotiate
                      </Button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {activeTab === 'negotiations' && (
          <div className="text-center py-12">
            <MessageSquare className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-400 mb-2">No Active Negotiations</h3>
            <p className="text-gray-500 mb-6">Start diplomatic discussions with other factions</p>
            <Button className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
              <MessageSquare className="w-4 h-4 mr-2" />
              Initiate Negotiation
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

export default Diplomacy